import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ShieldCheck, PlusCircle, Store, ClipboardList, Landmark } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { StatePill } from "@/components/membership/Entitlement";
import { UpgradeModal } from "@/components/membership/UpgradeModal";
import { useEntitlements } from "@/hooks/useEntitlements";
import { Loading, Stat } from "@/pages/_shared";
import api from "@/lib/api";

function Section({ icon: Icon, title, note, badge, children }) {
  return (
    <section className="mt-8 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-head text-lg font-extrabold text-slate-900"><Icon size={17} className="text-[#1a5fd0]" /> {title}</h2>
        {badge}
      </div>
      <p className="mt-0.5 text-[13px] text-slate-500">{note}</p>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function ReportedBlock({ reported, onUpgrade }) {
  const st = reported.state;
  return (
    <Card className="card-spotlight p-5" data-testid="tradelines-reported">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-head text-[14px] font-bold text-slate-900">{reported.provider} file</div>
          <div className="mt-0.5 text-[12px] text-slate-500">Tradelines furnished to the bureau by your creditors.</div>
        </div>
        <StatePill state={st} />
      </div>
      {st === "upgrade_required" && (
        <button onClick={onUpgrade} data-testid="tradelines-upgrade-btn" className="mt-4 rounded-lg bg-[#b0862f] px-4 py-2 font-head text-[13px] font-bold text-white hover:opacity-90">Upgrade to {reported.required_plan}</button>
      )}
      {st === "not_available" && <p className="mt-4 text-[12px] text-slate-400">MMT hasn't enabled this bureau connection yet. Your reported tradelines appear here automatically once it goes live — never estimated.</p>}
      {st === "not_connected" && <p className="mt-4 text-[12px] text-[#1a5fd0]">Connect your bureau file from Business Credit to pull reported accounts.</p>}
      {st === "connected" && reported.items.length === 0 && <p className="mt-4 text-[12px] text-slate-500" data-testid="tradelines-reported-empty">Your bureau file is connected but no tradelines have been furnished yet.</p>}
      {st === "connected" && reported.items.length > 0 && (
        <div className="mt-4 divide-y divide-slate-100">
          {reported.items.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-2 text-[13px]" data-testid="reported-tradeline-row">
              <span className="font-semibold text-slate-800">{t.creditor}</span>
              <span className="font-mono text-[11px] text-slate-500">{t.payment_manner || "—"} · {t.source}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function EnrolledRow({ e }) {
  const state = e.verified_reporting ? "connected" : e.action_required ? "needs_attention" : "connection_pending";
  return (
    <div className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center" data-testid="enrolled-tradeline-row">
      <div className="flex w-56 shrink-0 items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><Building2 size={17} /></span>
        <div>
          <div className="text-[13px] font-bold text-slate-900">{e.name}</div>
          <div className="text-[11px] text-slate-400">{e.category}</div>
        </div>
      </div>
      <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] uppercase text-slate-500">{e.terms}</span>
      <div className="flex-1 text-[12px] text-slate-500">{e.reporting_status}{e.on_time_payments != null && ` · ${e.on_time_payments} on-time payments logged`}</div>
      <div className="flex w-44 shrink-0 flex-col items-start gap-1 sm:items-end">
        <StatePill state={state} labelOverride={e.verified_reporting ? "Reporting confirmed" : e.action_required ? "Action required" : "Enrolled"} />
        <span className="text-[11px] text-slate-400">Reports to: {(e.reports_to || []).join(", ") || "—"}</span>
      </div>
    </div>
  );
}

export default function TradeLines() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(false);
  const { ent, reload } = useEntitlements();
  const navigate = useNavigate();
  useEffect(() => { api.get("/tradelines/me").then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, []);
  if (!data) return <Loading />;
  if (data.error) return <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-[13px] text-red-700" data-testid="tradelines-error">We couldn't load your tradelines. Please refresh.</div>;

  const { reported, enrolled, marketplace, notes } = data;
  return (
    <div className="mx-auto max-w-4xl animate-fade-up pb-10" data-testid="trade-lines-page">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Trade<span className="text-foil">lines</span></h1>
      <p className="mt-1 max-w-2xl text-[14px] text-slate-500">Three things people confuse — kept separate here: what the bureaus actually <b>report</b>, what you've <b>enrolled</b> in through MMT, and what you <b>could add</b>. Membership → enrollment → reporting are separate events.</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <Stat label="Reported tradelines" value={reported.state === "connected" ? reported.items.length : "—"} sub={reported.state === "connected" ? reported.provider : "Bureau not connected"} />
        <Stat label="MMT enrollments" value={enrolled.counts.total} sub={`${enrolled.counts.reporting_confirmed} reporting confirmed`} tone="blue" />
        <Stat label="Needs action" value={enrolled.counts.action_required} tone={enrolled.counts.action_required ? "red" : "green"} sub={enrolled.counts.action_required ? "Open Business Credit" : "Nothing pending"} />
      </div>

      <Section icon={Landmark} title="Reported tradelines" note={notes.reported} badge={<span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Source: bureau</span>}>
        <ReportedBlock reported={reported} onUpgrade={() => setModal(true)} />
      </Section>

      <Section icon={ClipboardList} title="MMT enrollments" note={notes.enrolled} badge={<span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Source: MMT</span>}>
        {enrolled.items.length === 0 ? (
          <Card className="p-6 text-center" data-testid="tradelines-enrolled-empty">
            <div className="text-[14px] font-semibold text-slate-800">No enrollments yet</div>
            <p className="mt-1 text-[13px] text-slate-500">Add a vendor from the marketplace below or start a Credit Builder program.</p>
          </Card>
        ) : (
          <Card className="divide-y divide-slate-100 px-5">{enrolled.items.map((e) => <EnrolledRow key={e.id} e={e} />)}</Card>
        )}
      </Section>

      <Section icon={Store} title="Marketplace — vendors you can add" note={notes.marketplace} badge={<span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Not yet yours</span>}>
        <div className="grid gap-3 sm:grid-cols-3">
          {marketplace.items.map((v, i) => (
            <Card key={v.id} className="card-spotlight flex flex-col p-4 animate-fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid="marketplace-vendor-card">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-bold text-slate-900">{v.name}</span>
                <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] uppercase text-slate-500">{v.terms}</span>
              </div>
              <div className="mt-0.5 text-[11px] text-slate-400">{v.category}</div>
              <p className="mt-2 flex-1 text-[12px] text-slate-600">{v.note || `Reports to ${v.reports_to.join(", ")}.`}</p>
              <div className="mt-2 text-[11px] text-slate-400">Reports to: {v.reports_to.join(", ")}</div>
              {v.already_enrolled ? (
                <span className="mt-3 text-center font-mono text-[10px] uppercase tracking-wider text-emerald-600">Enrolled</span>
              ) : (
                <button onClick={() => navigate(marketplace.setup_route)} data-testid="add-tradeline-btn"
                  className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#1a5fd0]/20 bg-[#1a5fd0]/5 px-3 py-1.5 text-[12px] font-semibold text-[#1a5fd0] transition-colors hover:bg-[#1a5fd0]/10">
                  <PlusCircle size={14} /> Add via MMT setup
                </button>
              )}
            </Card>
          ))}
        </div>
      </Section>

      <p className="mt-8 flex items-center justify-center gap-2 text-center text-[12px] text-slate-400"><ShieldCheck size={14} /> Reporting is shown only when a vendor or bureau confirms it. Enrollment alone is never displayed as reporting.</p>
      {modal && ent && <UpgradeModal open onClose={() => { setModal(false); reload(); }} feature="business_credit.dnb" requiredPlan="Build" returnRoute="/trade-lines" ent={ent} />}
    </div>
  );
}
